import { routing } from '@/i18n/routing';

/**
 * Permissions that can open the container detail screen, mirroring the
 * `role_or_permission` middleware its reads sit behind
 * (`backend/routes/api/admin/registrations.php`, `$containerDetailReaders`).
 *
 * Every stage listing's row action lands here, so guarding the route on
 * `registrations.view` alone locked out roles the API itself allows: Page
 * Sticking's view icon opened a full-page "Access Denied" for a role whose
 * Container Labeling view permission was explicitly on (OMA-602).
 */
export const CONTAINER_DETAIL_PERMISSIONS = [
  'registrations.view',
  'page_indexes.view',
  'review.view',
  'boxes.view',
  'supervisor.view',
  'search.view',
  'photo_scanning.view',
  'ocr.view'
] as const;

export interface RouteAccessRule {
  /**
   * Locale-less path pattern. `:param` matches exactly one segment, mirroring
   * the `[param]` folders under `src/app/[locale]/dashboard`.
   */
  pattern: string;
  /** OR logic — one of these is enough. Empty = any authenticated user. */
  permissions: readonly string[];
  /** App-setting key that must be truthy for the route to be reachable. */
  featureFlag?: string;
}

/**
 * The one place that answers "what does this URL need?".
 *
 * Page guards (`AuthGuard requiredPermissions`), sidebar visibility
 * (`nav-config.ts`) and the post-login redirect used to each carry their own
 * copy of this mapping, and drift between them is what produces the whole
 * "Access Denied on a page the UI just sent me to" bug family (OMA-601..605).
 * `tests/unit/route-access.test.ts` fails the build when a page, a nav item or
 * this table disagree, and when a dashboard route is missing here entirely.
 *
 * Ordering is irrelevant: `resolveRouteAccess` picks the most specific match,
 * so `/dashboard/users/new` wins over `/dashboard/users/:id`.
 */
export const ROUTE_ACCESS_RULES: readonly RouteAccessRule[] = [
  { pattern: '/dashboard', permissions: ['dashboard.view'] },
  { pattern: '/dashboard/search', permissions: ['search.view'] },

  // External lab
  { pattern: '/dashboard/registration', permissions: ['registrations.view'] },
  {
    pattern: '/dashboard/registration/new',
    permissions: ['registrations.create']
  },
  { pattern: '/dashboard/sticking', permissions: ['page_indexes.view'] },
  { pattern: '/dashboard/sticking/:id', permissions: ['page_indexes.view'] },
  { pattern: '/dashboard/review', permissions: ['review.view'] },
  { pattern: '/dashboard/review/:id', permissions: ['review.view'] },
  // `supervisor.view` reads boxes on the API (`routes/api/admin/boxes.php`),
  // and the Supervisor panel's Packing tab links straight at these screens.
  {
    pattern: '/dashboard/packing',
    permissions: ['boxes.view', 'supervisor.view']
  },
  { pattern: '/dashboard/packing/new', permissions: ['boxes.create'] },
  {
    pattern: '/dashboard/packing/:id',
    permissions: ['boxes.view', 'supervisor.view']
  },
  // `/shipments` and `/shipments/{id}` accept `supervisor.view`, and the
  // Supervisor panel's Shipping tab links at both screens. Creating a shipment
  // stays on `shipments.create` — the API says the same.
  {
    pattern: '/dashboard/shipping',
    permissions: ['shipments.view', 'supervisor.view']
  },
  { pattern: '/dashboard/shipping/new', permissions: ['shipments.create'] },
  {
    pattern: '/dashboard/shipping/:id',
    permissions: ['shipments.view', 'supervisor.view']
  },

  // Internal lab
  { pattern: '/dashboard/shipment-statement', permissions: ['reception.view'] },
  {
    pattern: '/dashboard/shipment-statement/initialize',
    permissions: ['reception.edit']
  },
  {
    pattern: '/dashboard/shipment-statement/:id',
    permissions: ['reception.view']
  },
  {
    pattern: '/dashboard/shipment-statement/:id/continue',
    permissions: ['reception.edit']
  },
  { pattern: '/dashboard/sorting', permissions: ['sorting.view'] },
  { pattern: '/dashboard/sorting/:id', permissions: ['sorting.view'] },
  {
    pattern: '/dashboard/handover-scanning',
    permissions: ['handover_scanning.view']
  },
  {
    pattern: '/dashboard/handover-scanning/:id',
    permissions: ['handover_scanning.view']
  },
  {
    pattern: '/dashboard/photo-scanning',
    permissions: ['photo_scanning.view']
  },
  {
    pattern: '/dashboard/photo-scanning/:id',
    permissions: ['photo_scanning.view']
  },
  { pattern: '/dashboard/qr-review', permissions: ['qr_review.view'] },
  { pattern: '/dashboard/qr-review/:id', permissions: ['qr_review.view'] },
  { pattern: '/dashboard/ocr-containers', permissions: ['ocr.view'] },
  { pattern: '/dashboard/ocr-containers/:id', permissions: ['ocr.view'] },
  { pattern: '/dashboard/qa-ocr-1', permissions: ['qa1_ocr.view'] },
  { pattern: '/dashboard/qa-ocr-1/:id', permissions: ['qa1_ocr.view'] },
  { pattern: '/dashboard/qa-ocr-2', permissions: ['qa2_ocr.view'] },
  { pattern: '/dashboard/qa-ocr-2/:id', permissions: ['qa2_ocr.view'] },
  { pattern: '/dashboard/qa-ocr-3', permissions: ['qa3_ocr.view'] },
  { pattern: '/dashboard/qa-ocr-3/:id', permissions: ['qa3_ocr.view'] },
  // Reading shelves is not operating on them: `$shelfViewers` in
  // `routes/api/admin/shelves.php` accepts all three, and every operate action
  // on the screen gates itself on `warehouse.operate`.
  {
    pattern: '/dashboard/warehouse',
    permissions: ['warehouse.operate', 'shelves.view', 'supervisor.view']
  },
  {
    pattern: '/dashboard/warehouse/:shelfId/containers',
    permissions: ['warehouse.operate', 'shelves.view', 'supervisor.view']
  },
  {
    pattern: '/dashboard/warehouse-assignment',
    permissions: ['warehouse.operate']
  },

  // Repair management
  { pattern: '/dashboard/repairs', permissions: ['repairs.view'] },
  { pattern: '/dashboard/repairs/:id/print', permissions: ['repairs.view'] },
  {
    pattern: '/dashboard/repair-settings',
    permissions: ['repair_settings.view']
  },

  // Shared detail screen — every stage listing's row action lands here.
  {
    pattern: '/dashboard/containers/:id',
    permissions: CONTAINER_DETAIL_PERMISSIONS
  },

  // Administration
  { pattern: '/dashboard/supervisor', permissions: ['supervisor.view'] },
  // The discovered-field review screen. Its own route rather than an overlay
  // on the dashboard: it renders the container's full OCR results panel —
  // document viewer included — which needs the whole page.
  {
    pattern: '/dashboard/supervisor/discovered-fields/:containerId',
    permissions: ['supervisor.view']
  },
  { pattern: '/dashboard/clients', permissions: ['clients.view'] },
  {
    pattern: '/dashboard/form-management',
    permissions: ['dynamic_forms.view']
  },
  { pattern: '/dashboard/ocr-templates', permissions: ['ocr_templates.view'] },
  {
    pattern: '/dashboard/ocr-templates/:id/configure',
    permissions: ['ocr_templates.edit']
  },
  { pattern: '/dashboard/roles', permissions: ['roles.view'] },
  { pattern: '/dashboard/users', permissions: ['users.view'] },
  { pattern: '/dashboard/users/new', permissions: ['users.create'] },
  { pattern: '/dashboard/users/:id/edit', permissions: ['users.edit'] },
  { pattern: '/dashboard/users/:id/profile', permissions: ['users.edit'] },

  // System settings
  {
    pattern: '/dashboard/storage-locations',
    permissions: ['storage_locations.view']
  },
  {
    pattern: '/dashboard/storage-locations/new',
    permissions: ['storage_locations.create']
  },
  { pattern: '/dashboard/shelves', permissions: ['shelves.view'] },
  { pattern: '/dashboard/box-types', permissions: ['box_types.view'] },
  {
    pattern: '/dashboard/container-types',
    permissions: ['container_types.view']
  },
  { pattern: '/dashboard/devices', permissions: ['devices.view'] },
  // The Supervisor panel's log drawer links here; `/audit-logs` accepts both.
  {
    pattern: '/dashboard/system-logs',
    permissions: ['system_logs.view', 'supervisor.view']
  },
  { pattern: '/dashboard/localization', permissions: ['localization.view'] },
  {
    pattern: '/dashboard/configurations',
    permissions: ['configurations.view']
  },
  { pattern: '/dashboard/ai-models', permissions: ['ai_models.view'] },
  {
    pattern: '/dashboard/rejection-reasons',
    permissions: ['rejection_reasons.view']
  },
  { pattern: '/dashboard/labs', permissions: ['labs.view'] },

  // Open to any authenticated account
  { pattern: '/dashboard/about', permissions: [] },
  { pattern: '/dashboard/settings/account', permissions: [] },
  { pattern: '/dashboard/settings/security', permissions: [] },
  { pattern: '/dashboard/dev-tools', permissions: [] }
];

/** Strips `?query`, `#hash` and a leading locale segment. */
export function normalizeRoutePath(path: string): string {
  let normalized = path.split('?')[0].split('#')[0];

  for (const locale of routing.locales) {
    if (normalized === `/${locale}`) return '/';
    if (normalized.startsWith(`/${locale}/`)) {
      normalized = normalized.slice(locale.length + 1);
      break;
    }
  }

  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
}

function matches(pattern: string, segments: string[]): boolean {
  const patternSegments = pattern.split('/').filter(Boolean);
  if (patternSegments.length !== segments.length) return false;
  return patternSegments.every(
    (segment, i) => segment.startsWith(':') || segment === segments[i]
  );
}

/** Count of literal segments — higher wins, so `/users/new` beats `/users/:id`. */
function specificity(pattern: string): number {
  return pattern
    .split('/')
    .filter(Boolean)
    .filter((segment) => !segment.startsWith(':')).length;
}

/** The rule governing a path, or `null` when the path isn't a known route. */
export function resolveRouteAccess(path: string): RouteAccessRule | null {
  const segments = normalizeRoutePath(path).split('/').filter(Boolean);

  let best: RouteAccessRule | null = null;
  for (const rule of ROUTE_ACCESS_RULES) {
    if (!matches(rule.pattern, segments)) continue;
    if (!best || specificity(rule.pattern) > specificity(best.pattern)) {
      best = rule;
    }
  }

  return best;
}
